import { 
  AlertCircle, 
  Ban, 
  Calendar, 
  Check, 
  ExternalLink, 
  Pencil,
  RotateCcw, 
  Trash2,
  X
} from "../../../components/icons";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from "../../../components/ui/popover";
import { TableCell, TableRow } from "../../../components/ui/table";
import { centsToCurrency } from "../../utils/money";
import { formatDate } from "../../utils/date";
import { useState } from "react";

interface FinancialRowProps {
  item: any;
  lang: "pt" | "en";
  t: any;
  insuranceProviders: any[];
  resolveSpecialtyName: (p: any) => string;
  handleMarkAsPaid: (p: any) => void;
  handleDelete: (p: any) => void;
  handleStopRecurrence: (id: number) => void;
  onEditAmount: (item: any, newAmountCents: number) => void;
}

export function FinancialRow({
  item,
  lang,
  t,
  insuranceProviders,
  resolveSpecialtyName,
  handleMarkAsPaid,
  handleDelete,
  handleStopRecurrence,
  onEditAmount,
}: FinancialRowProps) {
  const p = item;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>("");

  const startEdit = () => {
    setEditValue((p.amount_cents / 100).toFixed(2).replace('.', ','));
    setIsEditing(true);
  };

  const saveEdit = async () => {
    const value = parseFloat(editValue.replace(',', '.'));
    if (isNaN(value) || value < 0) return;
    const cents = Math.round(value * 100);
    await onEditAmount(p, cents);
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditValue("");
  };
  return (
    <TableRow key={p.id} className="group cursor-default">
      <TableCell className="pl-6 py-4">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-neutral-900 group-hover:text-primary transition-colors">
            {p.display_name}
          </div>
          {p.is_income && p.client_uid && (
            <a 
              href={`/app/clients/${p.client_uid}`}
              className="p-1 rounded-md hover:bg-neutral-100 text-neutral-400 hover:text-primary transition-all"
              title={lang === 'pt' ? "Ver cliente" : "View client"}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {!!p.is_recurring ? <RotateCcw className="h-3 w-3 text-blue-500 animate-pulse" title="Recorrente" /> : null}
        </div>
        <div className="flex flex-col gap-0.5 mt-0.5">
          <div className="text-[10px] text-primary font-bold">
            {resolveSpecialtyName(p)}
          </div>
          {p.plan_name && p.plan_name !== resolveSpecialtyName(p) && (
            <div className="text-[9px] text-neutral-400 font-medium">
              {p.plan_name}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge variant="secondary" className="text-[10px] px-2 py-0 w-fit">
            {p.is_income 
              ? (p.type === 'monthly' ? t.types.monthly : t.types.per_consultation)
              : (p.category || "Despesa")
            }
          </Badge>
          {p.payment_method && (
            <span className="text-[9px] text-neutral-400 font-bold uppercase ml-1">
              {p.payment_method}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {p.is_income ? (
          <div className="flex items-center gap-1.5">
            {p.insurance_provider_id && p.insurance_provider_id !== 1 ? (
              <>
                <span className="h-2 w-2 rounded-full bg-blue-400" />
                <span className="text-xs font-bold text-neutral-700">
                  {insuranceProviders.find(i => i.id === p.insurance_provider_id)?.name || "Convênio"}
                </span>
              </>
            ) : (
              <span className="text-[10px] text-neutral-300 font-medium uppercase italic">---</span>
            )}
          </div>
        ) : (
          <span className="text-[10px] text-neutral-300 font-medium uppercase italic">---</span>
        )}
      </TableCell>
      <TableCell className={`font-bold ${!p.is_income ? 'text-red-600' : 'text-neutral-900'}`}>
        {isEditing ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <input
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              autoFocus
              className="w-20 h-7 text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={saveEdit}
              className="p-1 hover:bg-green-50 rounded"
              title="Salvar"
            >
              <Check className="h-4 w-4 text-green-600" />
            </button>
            <button
              onClick={cancelEdit}
              className="p-1 hover:bg-red-50 rounded"
              title="Cancelar"
            >
              <X className="h-4 w-4 text-red-600" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span>{centsToCurrency(p.amount_cents, lang)}</span>
            <Pencil 
              className="h-3 w-3 text-neutral-400 hover:text-primary cursor-pointer transition-colors" 
              onClick={(e) => { e.stopPropagation(); startEdit(); }}
            />
          </div>
        )}
      </TableCell>
      <TableCell className="text-neutral-500 text-xs">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            <span className="text-[10px] font-bold uppercase text-neutral-400">
              {lang === 'pt' ? "Venc:" : "Due:"}
            </span>
            {formatDate(p.due_date || (p.status === 'pending' ? p.paid_at : p.period_start || p.paid_at || p.display_date))}
          </div>
          {p.is_income && p.status === 'paid' && p.paid_at && (
            <div className="flex items-center gap-1.5 text-green-600 font-medium">
              <Check className="h-3 w-3" />
              <span className="text-[10px] font-bold uppercase opacity-70">
                {lang === 'pt' ? "Pago:" : "Paid:"}
              </span>
              {formatDate(p.paid_at)}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge 
          variant={p.status === 'paid' ? "success" : p.status === 'insurance_pending' ? "secondary" : "warning"}
          className={`text-[10px] font-bold uppercase tracking-wider ${p.status === 'insurance_pending' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}`}
        >
          {p.status === 'paid' ? t.filters.paid : p.status === 'insurance_pending' ? t.filters.insurance_pending : t.filters.pending}
        </Badge>
      </TableCell>
      <TableCell className="text-right pr-6">
        <div className="flex items-center justify-end gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                size="sm" 
                variant="ghost"
                className={`h-8 w-8 p-0 rounded-lg border border-transparent ${
                  p.status === 'paid' 
                    ? 'hover:bg-neutral-100 hover:text-neutral-600 hover:border-neutral-200' 
                    : 'hover:bg-green-50 hover:text-green-600 hover:border-green-100'
                }`}
                title={p.status === 'paid' ? (lang === 'pt' ? "Desmarcar" : "Unmark") : t.actions.markAsPaid}
              >
                {p.status === 'paid' ? <RotateCcw className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-4 shadow-xl border-neutral-200">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${p.status === 'paid' ? 'bg-neutral-100' : 'bg-green-50'}`}>
                    {p.status === 'paid' ? <RotateCcw className="h-5 w-5 text-neutral-600" /> : <Check className="h-5 w-5 text-green-600" />}
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-bold text-neutral-900">
                      {p.status === 'paid' 
                        ? (lang === 'pt' ? "Desmarcar?" : "Unmark?") 
                        : (lang === 'pt' ? "Confirmar?" : "Confirm?")
                      }
                    </div>
                    <div className="text-xs text-neutral-500 leading-relaxed">
                      {p.status === 'paid'
                        ? (lang === 'pt' ? "O status voltará para 'Em aberto'." : "Status will return to 'Pending'.")
                        : (lang === 'pt' ? "Deseja marcar como pago?" : "Mark as paid?")
                      }
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <PopoverClose asChild>
                    <Button variant="ghost" size="sm" className="h-8 text-xs px-3">
                      {lang === 'pt' ? "Cancelar" : "Cancel"}
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <Button 
                      variant={p.status === 'paid' ? "secondary" : "default"} 
                      size="sm" 
                      className={`h-8 text-xs px-3 shadow-sm ${p.status === 'paid' ? '' : 'bg-green-600 hover:bg-green-700 text-white shadow-green-100 font-bold'}`}
                      onClick={() => handleMarkAsPaid(p)}
                    >
                      {p.status === 'paid' 
                        ? (lang === 'pt' ? "Confirmar" : "Unmark") 
                        : (lang === 'pt' ? "Confirmar" : "Confirm")
                      }
                    </Button>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {p.is_expense && !!p.is_recurring && !p.recurrence_end_at && (
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="h-8 w-8 p-0 rounded-lg hover:bg-blue-50 hover:text-blue-600 border border-transparent hover:border-blue-100"
                  title={t.actions.stopRecurrence}
                >
                  <Ban className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4 shadow-xl border-neutral-200">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Ban className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-neutral-900">
                        {t.actions.stopRecurrence}?
                      </div>
                      <div className="text-xs text-neutral-500 leading-relaxed">
                        {lang === 'pt' 
                          ? "Esta despesa deixará de aparecer automaticamente nos próximos meses." 
                          : "This expense will no longer appear automatically in future months."}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <PopoverClose asChild>
                      <Button variant="ghost" size="sm" className="h-8 text-xs px-3">
                        {lang === 'pt' ? "Cancelar" : "Cancel"}
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button 
                        size="sm" 
                        className="h-8 text-xs px-3 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                        onClick={() => handleStopRecurrence(p.original_id || p.id)}
                      >
                        {lang === 'pt' ? "Confirmar" : "Confirm"}
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          <Popover>
            <PopoverTrigger asChild>
              <Button 
                size="sm" 
                variant="ghost"
                className="h-8 w-8 p-0 rounded-lg hover:bg-red-50 hover:text-red-600 border border-transparent hover:border-red-100"
                title={t.actions.delete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-4 shadow-xl border-neutral-200">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-bold text-neutral-900">
                      {lang === 'pt' ? "Confirmar exclusão" : "Confirm deletion"}
                    </div>
                    <div className="text-xs text-neutral-500 leading-relaxed">
                      {lang === 'pt' 
                        ? "Deseja excluir este registro? Esta ação não pode ser desfeita." 
                        : "Delete this record? This cannot be undone."}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <PopoverClose asChild>
                    <Button variant="ghost" size="sm" className="h-8 text-xs px-3">
                      {lang === 'pt' ? "Cancelar" : "Cancel"}
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <Button 
                      size="sm" 
                      className="h-8 text-xs px-3 bg-red-600 hover:bg-red-700 text-white font-bold shadow-sm shadow-red-200"
                      onClick={() => handleDelete(p)}
                    >
                      {t.actions.delete}
                    </Button>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </TableCell>
    </TableRow>
  );
}
